<template>
  <teleport to="body">
    <div v-if="visible" class="cm-root">
      <div class="cm-highlight" :class="{ round }" :style="highlightStyle"></div>
      <div class="cm-tip" :style="tipStyle">
        <div class="cm-tip-title">{{ title }}</div>
        <div class="cm-tip-text">{{ text }}</div>
        <button type="button" class="cm-tip-btn" @click="dismiss">{{ buttonText }}</button>
      </div>
    </div>
  </teleport>
</template>

<script>
import { loadWizards, wizardSeen, markWizard, wizardsState } from '@/plugins/wizards';

// Solo un coachmark visible a la vez (los demás esperan a que se cierre)
let activeKey = null;

export default {
  name: 'Coachmark',
  props: {
    storageKey: { type: String, required: true },
    target: { type: String, required: true },
    title: { type: String, default: '' },
    text: { type: String, default: '' },
    buttonText: { type: String, default: 'Entendido' },
    placement: { type: String, default: 'top' }, // top | bottom | left | top-left
    round: { type: Boolean, default: false },
    delay: { type: Number, default: 700 },
  },
  data() {
    return { visible: false, rect: null, poll: null, targetEl: null };
  },
  computed: {
    highlightStyle() {
      if (!this.rect) return { display: 'none' };
      const p = 6;
      return {
        top: (this.rect.top - p) + 'px',
        left: (this.rect.left - p) + 'px',
        width: (this.rect.width + p * 2) + 'px',
        height: (this.rect.height + p * 2) + 'px',
      };
    },
    tipStyle() {
      if (!this.rect) return { display: 'none' };
      const t = this.rect;
      const vw = window.innerWidth, vh = window.innerHeight;
      switch (this.placement) {
        case 'top-left':
          return { bottom: (vh - t.top + 16) + 'px', right: (vw - t.right) + 'px' };
        case 'left':
          return { top: Math.min(vh - 150, Math.max(16, t.top + t.height / 2 - 60)) + 'px', right: (vw - t.left + 16) + 'px' };
        case 'bottom':
          return { top: (t.bottom + 16) + 'px', left: Math.max(16, t.left) + 'px' };
        default: // top
          return { bottom: (vh - t.top + 16) + 'px', left: Math.max(16, t.left) + 'px' };
      }
    },
  },
  mounted() {
    loadWizards();
    this.poll = setInterval(this.tryShow, 400);
    this.onReposition = () => { if (this.visible && this.targetEl) this.rect = this.targetEl.getBoundingClientRect(); };
    window.addEventListener('resize', this.onReposition);
    window.addEventListener('scroll', this.onReposition, true);
  },
  beforeUnmount() {
    clearInterval(this.poll);
    if (activeKey === this.storageKey) activeKey = null;
    window.removeEventListener('resize', this.onReposition);
    window.removeEventListener('scroll', this.onReposition, true);
  },
  methods: {
    tryShow() {
      if (!wizardsState.loaded) return; // espera a saber qué ha visto el usuario
      if (wizardSeen(this.storageKey)) { clearInterval(this.poll); return; }
      const el = document.querySelector(this.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.height < 20 || r.width < 20) return; // aún no renderizado / vacío
      if (activeKey && activeKey !== this.storageKey) return; // espera a que se cierre el otro
      clearInterval(this.poll);
      activeKey = this.storageKey;
      this.targetEl = el;
      this.rect = r;
      setTimeout(() => { this.visible = true; }, this.delay);
      // Al interactuar con el objetivo, se da por visto
      el.addEventListener('click', this.dismiss, { once: true });
    },
    dismiss() {
      this.visible = false;
      if (activeKey === this.storageKey) activeKey = null;
      markWizard(this.storageKey);
      clearInterval(this.poll);
    },
  },
};
</script>

<style scoped>
.cm-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  pointer-events: none;
}
.cm-highlight {
  position: fixed;
  border-radius: 12px;
  border: 2px solid #00e187;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 4px rgba(0, 225, 135, 0.35);
  animation: cm-pulse 1.6s ease-in-out infinite;
  pointer-events: none;
}
.cm-highlight.round { border-radius: 50%; }
@keyframes cm-pulse {
  0%, 100% { box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 4px rgba(0, 225, 135, 0.35); }
  50% { box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55), 0 0 0 9px rgba(0, 225, 135, 0.15); }
}

.cm-tip {
  position: fixed;
  z-index: 2001;
  max-width: 290px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.35);
  padding: 16px 18px;
  pointer-events: auto;
  animation: cm-in .2s ease;
}
@keyframes cm-in {
  from { transform: translateY(6px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
.cm-tip-title {
  font-size: 15px;
  font-weight: 700;
  color: #1f2733;
  margin-bottom: 5px;
}
.cm-tip-text {
  font-size: 13px;
  line-height: 1.5;
  color: #56606e;
  margin-bottom: 14px;
}
.cm-tip-btn {
  background: #00cba9;
  color: #fff;
  border: none;
  border-radius: 9px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background .15s;
}
.cm-tip-btn:hover { background: #0b7a6a; }
</style>
